import { AuthTokenPage } from './auth-token/AuthTokenPage'
import { CourierSignupPage } from './courier-signup/CourierSignupPage'
import { LoginPage } from './LoginPage'
import { NotFoundPage } from './NotFoundPage'
import { ProfilePage } from './profile/ProfilePage'

/**
 * `manager-auth` — the screens that live outside the shell, plus the 404.
 *
 * `/profile` is the one route every signed-in person may open, whatever their
 * role — it has no guard because there is nothing on it that belongs to anybody
 * else. `@RequireAuth` alone on all nine of its endpoints, and `self()` decides
 * whose rows come back.
 *
 * `/auth-token` is the Control Tower → manager handoff. §14.5 asked that the
 * exact flow be specified before porting, because it mints a session from a URL
 * parameter; `auth-token/handoff.ts` is that spec, and FB-179 is what writing it
 * turned up — the cached user survived the jump, so the shell painted the
 * previous account's menu under the new account's token.
 *
 * Every page of this module is ported.
 */
export const managerAuthModule = {
  name: 'manager-auth',
  routes: [
    {
      path: '/login',
      page: 'login',
      requiresAuth: false,
      component: LoginPage,
    },
    {
      // Outside the shell and outside the auth gate: it runs before there is a
      // session, which is the whole point of it.
      path: '/auth-token',
      page: 'auth-token',
      requiresAuth: false,
      component: AuthTokenPage,
    },
    {
      // §9.786, owner O139 - an external courier signs up here, before there is a session, from the link the
      // restaurant sends him from *Externý šofér*. The server refuses it for a franchise without the module.
      path: '/kurier',
      page: 'courier-signup',
      requiresAuth: false,
      component: CourierSignupPage,
    },
    {
      // No guard: every role may read and edit their own profile, and the server
      // scopes every one of the nine endpoints to the caller.
      path: '/profile',
      page: 'profile',
      component: ProfilePage,
    },
    {
      path: '*',
      page: 'not-found',
      component: NotFoundPage,
    },
  ],
} as const

export { AuthTokenPage, CourierSignupPage, LoginPage, NotFoundPage, ProfilePage }
